<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { BaseIcon } from '@cplug/design-system/components'
import { BaseIconColor, BaseIconName, BaseIconSize } from '@cplug/design-system/components/props'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { toggleOctadeskChat } from '~/domain/shared/support/composables/useOctadeskChat'
import useOctadeskChatStore from '~/domain/shared/support/stores/useOctadeskChatStore'
import { FLOATING_CHAT_ACTIONS_Z_INDEX } from '~/domain/shared/support/constants/octadeskConstants'
import { useFloatingChatActionsLayout } from '~/domain/shared/support/composables/useFloatingChatActionsLayout'
import useSupportStore from '~/domain/shared/support/stores/useSupportStore'
import { useThemePreferenceStore } from '~/domain/dashboard/settings/erp/shared'

useFloatingChatActionsLayout()

const { t } = useI18n()
const route = useRoute()
const authStore = useAuthenticationStore()
const supportStore = useSupportStore()
const themePreferenceStore = useThemePreferenceStore()
const { logged } = storeToRefs(authStore)
const { isSupportActive, hasSupportSubdomain } = storeToRefs(supportStore)
const { isDark } = storeToRefs(themePreferenceStore)
const { isOpen: isOctadeskChatOpen } = storeToRefs(useOctadeskChatStore())

const isAuthRoute = computed(() =>
  route.path.startsWith('/auth') || route.path.startsWith('/login')
)

const showSupportFab = computed(() =>
  Boolean(logged.value && isSupportActive.value && hasSupportSubdomain.value && !isAuthRoute.value)
)

const isVisible = computed(() => showSupportFab.value)

const fabBaseClasses = [
  'relative z-[1] flex size-14 items-center justify-center rounded-full border-0',
  'cursor-pointer pointer-events-auto',
  'shadow-[0_4px_14px_rgb(0_0_0/25%)] transition-[transform,box-shadow] duration-150 ease-out',
  'hover:scale-105 hover:shadow-[0_6px_18px_rgb(0_0_0/30%)] active:scale-95'
].join(' ')
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="floating-chat-actions fixed right-6 bottom-[var(--floating-chat-actions-bottom,1.5rem)] flex flex-row items-center gap-3 transition-[right,bottom] duration-300 ease-in-out pointer-events-auto isolate"
      :style="{ zIndex: FLOATING_CHAT_ACTIONS_Z_INDEX }"
      data-testid="floating-chat-actions"
    >
      <button
        v-if="showSupportFab"
        type="button"
        :class="[
          fabBaseClasses,
          isDark ? 'bg-white text-[#171717]' : 'bg-[#171717] text-white',
          isOctadeskChatOpen && 'shadow-[0_0_0_3px_rgb(33_150_243/35%),0_6px_18px_rgb(0_0_0/30%)]'
        ]"
        data-testid="btn-support-chat-fab"
        :aria-label="isOctadeskChatOpen ? t('CloseSupportChat') : t('OpenSupportChat')"
        @click.stop.prevent="toggleOctadeskChat"
      >
        <BaseIcon
          :name="BaseIconName.Headphones"
          :size="BaseIconSize.Medium"
          :color="BaseIconColor.Inherit"
        />
      </button>
    </div>
  </Teleport>
</template>

<style lang="scss">
html.assistant-sidebar-open .floating-chat-actions {
  right: calc(var(--assistant-sidebar-width, 400px) + 1.5rem);
}

html.assistant-sidebar-overlay .floating-chat-actions {
  display: none;
}

/* Launcher nativo oculto — usamos FloatingChatActions */
.octadesk-app button.octadesk-btn,
.octadesk-octachat-app > button,
.octadesk-app > button,
.octadesk-octachat-app button {
  display: none !important;
  pointer-events: none !important;
  width: 0 !important;
  height: 0 !important;
  opacity: 0 !important;
  visibility: hidden !important;
}
</style>

<i18n lang="yaml">
pt-br:
  OpenSupportChat: Abrir chat de suporte
  CloseSupportChat: Fechar chat de suporte
</i18n>
